import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Box, Button, Icon, Typography, CircularProgress } from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { ThemeContext } from "../../context/Themes";
import { AnalyticsService } from "../../services";
import { Heading, Text } from "../elements";

export interface DelayedProjectRow {
  id: string | number;
  projectId: string | number;
  projectName: string;
  expectedEndDate?: string;
  projectStatus?: string;
  delayDays: number;
}

interface ReduxState {
  user?: {
    user?: {
      company?: {
        id?: string | number;
      };
    };
  };
}

interface DelayedProjectsCardProps {
  companyId?: string | number | null;
  onViewAll?: () => void;
}

export const DelayedProjectsCard: React.FC<DelayedProjectsCardProps> = ({ companyId = null, onViewAll }) => {
  const { theme } = useContext(ThemeContext);
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [delayedProjects, setDelayedProjects] = useState<DelayedProjectRow[]>([]);

  const latestRequestId = useRef<number>(0);
  const userDetails = useSelector((state: ReduxState) => state.user?.user);
  const activeCompanyId = companyId || userDetails?.company?.id;

  const dark = {
    bg: "#1b2b4d",
    innerCardBg: "rgba(255, 255, 255, 0.03)",
    rowBg: "rgba(255, 255, 255, 0.03)",
    rowHoverBg: "rgba(255, 255, 255, 0.06)",
    rowBorder: "#2f3f61",
    border: "#2f3f61",
    hoverBorder: "#39496b",
    title: "#f0f0f0",
    text: "#94a3b8",
    headerText: "#f59e0b",
    iconBoxBg: "rgba(245, 158, 11, 0.12)",
    iconColor: "#f59e0b",
    pillBg: "rgba(239, 68, 68, 0.12)",
    pillBorder: "rgba(239, 68, 68, 0.25)",
    pillCount: "#ef4444",
    pillLabel: "rgba(239, 68, 68, 0.7)",
    tagBg: "rgba(239, 68, 68, 0.12)",
    tagText: "#ef4444",
  };

  const light = {
    bg: "#FFFFFF",
    innerCardBg: "#F8FAFC",
    rowBg: "#F8FAFC",
    rowHoverBg: "#F1F5F9",
    rowBorder: "#E2E8F0",
    border: "#E5E7EB",
    hoverBorder: "#cbd5e1",
    title: "#26364A",
    text: "#64748B",
    headerText: "#D97706",
    iconBoxBg: "#FFF7E6",
    iconColor: "#D97706",
    pillBg: "#FFF1F2",
    pillBorder: "#FECDD3",
    pillCount: "#E11D48",
    pillLabel: "#E11D48",
    tagBg: "#FFF1F2",
    tagText: "#E11D48",
  };

  const colors = theme !== "light_mode" ? dark : light;

  const fetchDelayedProjects = useCallback(async () => {
    const currentRequestId = ++latestRequestId.current;

    try {
      setLoading(true);
      setError(false);

      // Server computes "past end date and not DONE/CANCELLED" and sorts by delay.
      const { data } = await AnalyticsService.getDelayedProjects({
        ...(activeCompanyId ? { companyId: activeCompanyId } : {}),
      });

      if (currentRequestId !== latestRequestId.current) return;

      const rows: DelayedProjectRow[] = (data?.rows || []).map((row, index) => ({
        id: row.projectId ?? `delayed-fallback-${index}`,
        projectId: row.projectId ?? "",
        projectName: row.projectName || "Unnamed Project",
        expectedEndDate: row.expectedEndDate,
        projectStatus: row.projectStatus,
        delayDays: Number(row.delayDays) || 0,
      }));

      setDelayedProjects(rows);
    } catch (err: unknown) {
      if (currentRequestId === latestRequestId.current) {
        console.error("🚀 ~ fetchDelayedProjects error:", err);
        setError(true);
      }
    } finally {
      if (currentRequestId === latestRequestId.current) {
        setLoading(false);
      }
    }
  }, [activeCompanyId]);

  useEffect(() => {
    fetchDelayedProjects();
  }, [fetchDelayedProjects]);

  const hasData = !error && delayedProjects.length > 0;

  return (
    <Box
      className="mc-card"
      sx={{
        backgroundColor: colors.bg,
        border: `1px solid ${colors.border}`,
        borderRadius: "16px",
        p: 2.5,
        height: "100%",
        width: "100%",
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        boxShadow: theme === "light_mode" ? "0px 4px 16px rgba(0, 0, 0, 0.04)" : "none",
        transition: "all 0.3s ease",
      }}
    >
      {/* 1. Header Section */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 2,
          borderBottom: `1px solid ${colors.border}`,
          mb: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "8px",
              backgroundColor: colors.iconBoxBg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <WarningAmberRoundedIcon sx={{ color: colors.iconColor, fontSize: 18 }} />
          </Box>

          <Heading
            as="h3"
            style={{
              fontSize: "15px",
              fontWeight: 700,
              color: colors.headerText,
              lineHeight: 1,
            }}
          >
            Delayed Projects
          </Heading>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.6,
              backgroundColor: colors.pillBg,
              border: `1px solid ${colors.pillBorder}`,
              borderRadius: "18px",
              px: 1.5,
              py: 0.5,
            }}
          >
            <Typography
              component="span"
              sx={{
                fontSize: "16px",
                fontWeight: 800,
                color: colors.pillCount,
                lineHeight: 1,
              }}
            >
              {delayedProjects.length}
            </Typography>
            <Typography
              component="span"
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                color: colors.pillLabel,
                letterSpacing: "0.5px",
                lineHeight: 1,
              }}
            >
              TOTAL
            </Typography>
          </Box>

          <Button
            onClick={onViewAll ? onViewAll : () => navigate("/projects?status=INPROGRESS")}
            size="small"
            endIcon={<ChevronRightRoundedIcon sx={{ fontSize: "16px !important", ml: -0.5 }} />}
            sx={{
              color: colors.headerText,
              fontSize: "12px",
              fontWeight: 700,
              textTransform: "none",
              p: 0,
              minWidth: "auto",
              "&:hover": { backgroundColor: "transparent", opacity: 0.8 },
            }}
          >
            View All
          </Button>
        </Box>
      </Box>

      {/* 2. Content / Loading / Empty State Section */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1, py: 8 }}>
          <CircularProgress size={40} />
        </Box>
      ) : hasData ? (
        <Box
          sx={{
            flex: 1,
            maxHeight: "380px",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 1,
            pr: 0.5,
            "&::-webkit-scrollbar": { width: "5px" },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: colors.hoverBorder,
              borderRadius: "4px",
            },
          }}
        >
          {delayedProjects.map((item) => (
            <Box
              key={item.id}
              onClick={() => item.projectId && navigate(`/projects/${item.projectId}`)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: "10px 12px",
                borderRadius: "10px",
                backgroundColor: colors.rowBg,
                border: `1px solid ${colors.rowBorder}`,
                cursor: item.projectId ? "pointer" : "default",
                transition: "all 0.2s ease",
                "&:hover": item.projectId
                  ? {
                      borderColor: colors.hoverBorder,
                      backgroundColor: colors.rowHoverBg,
                    }
                  : {},
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0, flex: 1, pr: 1 }}>
                <Icon className="material-icons" style={{ color: colors.text, fontSize: "18px", flexShrink: 0 }}>
                  business
                </Icon>
                <Text
                  style={{
                    fontSize: "13px",
                    fontWeight: 600,
                    color: colors.title,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {item.projectName}
                </Text>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  backgroundColor: colors.tagBg,
                  px: 1,
                  py: 0.3,
                  borderRadius: "6px",
                  flexShrink: 0,
                }}
              >
                <AccessTimeRoundedIcon sx={{ fontSize: 13, color: colors.tagText }} />
                <Typography
                  component="span"
                  sx={{
                    fontSize: "12px",
                    fontWeight: 700,
                    color: colors.tagText,
                    lineHeight: 1,
                  }}
                >
                  {item.delayDays}d
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      ) : (
        <Box sx={{ py: 6, textAlign: "center", flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Text style={{ color: colors.text }}>No delayed projects found</Text>
        </Box>
      )}
    </Box>
  );
};

export default DelayedProjectsCard;
